import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  PHYSICAL_LOCATION_SORT_FIELDS,
  PHYSICAL_LOCATION_SORT_ORDERS,
  physicalLocationApi,
  type PhysicalLocation,
  type PhysicalLocationPageParams,
  type PhysicalLocationPageResponse,
  type PhysicalLocationSortField,
  type PhysicalLocationSortOrder,
} from '@/entities/Document';
import { DEFAULT_ORDER_OPTION, SHOW_ALL_ITEMS_QUANTITY } from '@/shared/config';
import type { OrderOptions, Pagination } from '@/shared/lib';
import {
  handleApiError,
  useInfiniteScrollObserver,
  useTableState,
  useUpdateUrl,
} from '@/shared/lib';

const PHYSICAL_LOCATION_ORDER_OPTIONS: OrderOptions[] = [
  { ...DEFAULT_ORDER_OPTION, label: 'Mais recente' },
  { label: 'Mais antigo', sort: 'createdAt', order: 'asc' },
  { label: 'Ordem alfabética crescente', sort: 'name', order: 'asc' },
  { label: 'Ordem alfabética decrescente', sort: 'name', order: 'desc' },
];

const isOneOf = <T extends readonly string[]>(value: string, options: T): value is T[number] =>
  options.some((option) => option === value);

const isPhysicalLocationSortField = (value: string): value is PhysicalLocationSortField =>
  isOneOf(value, PHYSICAL_LOCATION_SORT_FIELDS);

const isPhysicalLocationSortOrder = (value: string): value is PhysicalLocationSortOrder =>
  isOneOf(value, PHYSICAL_LOCATION_SORT_ORDERS);

const mergePhysicalLocations = (
  createdItems: PhysicalLocation[],
  existingItems: PhysicalLocation[]
): PhysicalLocation[] => {
  const seenIds = new Set<number>();

  return [...createdItems, ...existingItems].filter((item) => {
    if (seenIds.has(item.id)) {
      return false;
    }

    seenIds.add(item.id);
    return true;
  });
};

export function usePhysicalLocationsList() {
  const route = useRoute();
  const { updateUrl } = useUpdateUrl();
  const items = ref<PhysicalLocation[]>([]);
  const allItems = ref<PhysicalLocation[]>([]);
  const metadata = ref<Pagination | null>(null);
  const currentPage = ref(1);
  const searchTerm = ref('');
  const loading = ref(true);
  const loadingMore = ref(false);
  const hasMoreItems = ref(true);
  const error = ref<string | null>(null);
  const selectedQuantity = ref(10);
  const currentSort = ref<PhysicalLocationSortField | ''>('');
  const currentOrder = ref<PhysicalLocationSortOrder | ''>('');
  const orderOptions = PHYSICAL_LOCATION_ORDER_OPTIONS;

  const isInfiniteScrollMode = computed(() => selectedQuantity.value === SHOW_ALL_ITEMS_QUANTITY);

  const readQueryString = (value: unknown): string | undefined => {
    if (Array.isArray(value)) {
      return typeof value[0] === 'string' ? value[0] : undefined;
    }

    return typeof value === 'string' ? value : undefined;
  };

  const hydrateFromQuery = () => {
    const querySearchTerm = readQueryString(route.query.term);
    const querySort = readQueryString(route.query.sort);
    const queryOrder = readQueryString(route.query.order);

    searchTerm.value = querySearchTerm ?? '';
    currentSort.value = querySort && isPhysicalLocationSortField(querySort) ? querySort : '';
    currentOrder.value = queryOrder && isPhysicalLocationSortOrder(queryOrder) ? queryOrder : '';
  };

  const syncUrl = async () => {
    await updateUrl({
      search: searchTerm.value.trim(),
      sort:
        currentSort.value && currentOrder.value
          ? { sort: currentSort.value, order: currentOrder.value }
          : undefined,
    });
  };

  const { shouldShowError, shouldShowEmpty, shouldShowData, handleErrorToast } =
    useTableState<PhysicalLocation>({ error, data: items, loading });

  watch(error, (newError) => {
    if (newError) handleErrorToast();
  });

  const getHasMoreItems = (page: number, pageMetadata: Pagination | null) => {
    if (!pageMetadata) return false;

    if (typeof pageMetadata.isNextPage === 'boolean') {
      return pageMetadata.isNextPage;
    }

    return (pageMetadata.page ?? page) < (pageMetadata.totalPages ?? page);
  };

  const buildPageParams = (page: number): PhysicalLocationPageParams => {
    const params: PhysicalLocationPageParams = {
      page,
      itemsPerPage: selectedQuantity.value,
      term: searchTerm.value,
    };

    if (currentSort.value) {
      params.sort = currentSort.value;
    }

    if (currentSort.value && currentOrder.value) {
      params.order = currentOrder.value;
    }

    return params;
  };

  const resetPaginationState = () => {
    allItems.value = [];
    currentPage.value = 1;
    hasMoreItems.value = true;
  };

  const fetchPage = async (page: number) => {
    loading.value = true;
    error.value = null;

    try {
      const result: PhysicalLocationPageResponse =
        await physicalLocationApi.getPhysicalLocationsPage(buildPageParams(page));

      items.value = result.items;
      if (isInfiniteScrollMode.value) {
        allItems.value = result.items;
        hasMoreItems.value = getHasMoreItems(page, result.metadata);
      }

      metadata.value = result.metadata;
      currentPage.value = page;
    } catch (e) {
      error.value = handleApiError(e, 'Erro ao carregar locais').message;
    } finally {
      loading.value = false;
    }
  };

  const loadMore = async () => {
    if (!isInfiniteScrollMode.value || loadingMore.value || !hasMoreItems.value) {
      return;
    }

    loadingMore.value = true;
    error.value = null;

    const nextPage = currentPage.value + 1;

    try {
      const result: PhysicalLocationPageResponse =
        await physicalLocationApi.getPhysicalLocationsPage(buildPageParams(nextPage));

      items.value = result.items;
      allItems.value = [...allItems.value, ...result.items];
      metadata.value = result.metadata;
      currentPage.value = nextPage;
      hasMoreItems.value = getHasMoreItems(nextPage, result.metadata);
    } catch (e) {
      error.value = handleApiError(e, 'Erro ao carregar locais').message;
    } finally {
      loadingMore.value = false;
    }
  };

  useInfiniteScrollObserver({
    enabled: isInfiniteScrollMode,
    loadingMore,
    hasMore: hasMoreItems,
    onLoadMore: loadMore,
  });

  watch(selectedQuantity, async (newQuantity, oldQuantity) => {
    if (newQuantity === oldQuantity) return;

    resetPaginationState();
    await fetchPage(1);
  });

  const handleSearch = async (term: string) => {
    searchTerm.value = term;
    resetPaginationState();
    await syncUrl();
    await fetchPage(1);
  };

  const handleSort = async (sort: string, order: string) => {
    if (!isPhysicalLocationSortField(sort) || !isPhysicalLocationSortOrder(order)) {
      return;
    }

    currentSort.value = sort;
    currentOrder.value = order;
    resetPaginationState();
    await syncUrl();
    await fetchPage(1);
  };

  const insertCreatedItems = (createdItems: PhysicalLocation[]) => {
    if (createdItems.length === 0) {
      return;
    }

    const mergedAllItems = mergePhysicalLocations(createdItems, allItems.value);
    const mergedVisibleItems = mergePhysicalLocations(createdItems, items.value);

    allItems.value = mergedAllItems;
    items.value = isInfiniteScrollMode.value
      ? mergedAllItems
      : mergedVisibleItems.slice(0, selectedQuantity.value);

    if (metadata.value) {
      const updatedTotal = (metadata.value.total ?? items.value.length) + createdItems.length;

      metadata.value = {
        ...metadata.value,
        total: updatedTotal,
        totalItems: updatedTotal,
        totalPages: Math.max(1, Math.ceil(updatedTotal / selectedQuantity.value)),
      };
    }

    if (isInfiniteScrollMode.value) {
      hasMoreItems.value = allItems.value.length < (metadata.value?.total ?? allItems.value.length);
    }
  };

  const refreshList = async () => {
    if (isInfiniteScrollMode.value) {
      resetPaginationState();
      await fetchPage(1);
      return;
    }

    await fetchPage(currentPage.value);
  };

  onMounted(() => {
    hydrateFromQuery();
    fetchPage(1);
  });

  return {
    items,
    allItems,
    metadata,
    currentPage,
    searchTerm,
    loading,
    loadingMore,
    hasMoreItems,
    error,
    selectedQuantity,
    currentSort,
    currentOrder,
    orderOptions,
    handleSearch,
    handleSort,
    fetchPage,
    loadMore,
    shouldShowError,
    shouldShowEmpty,
    shouldShowData,
    insertCreatedItems,
    refreshList,
  };
}
